import {
  useState,
  useEffect,
  startTransition,
  useCallback,
  useTransition,
} from "react";
import PropTypes from "prop-types";
import { useStore } from "zustand";
import {
  CustomDialog,
  Heading,
  Content,
  ButtonGroup,
  Button,
  Text,
  ActionButton,
  ComboBox,
  ComboBoxItem,
  Avatar,
  CloseButton,
} from "@react-spectrum/s2";
import { useAsyncList } from "react-stately";

import { store } from "../../store";
import { suggest, accept, getPeople } from "@app/services/peopleGraph";
import { useShareStory, useUnshareStory } from "@app/hooks/stories";

import DeleteIcon from "@react-spectrum/s2/icons/Delete";
import BackIcon from "@react-spectrum/s2/icons/ChevronLeft";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const listStyle = style({
  display: "flex",
  flexDirection: "column",
  gap: 24,
  font: "ui",
});
const UserListItem = ({ user, children }) => {
  return (
    <div
      className={style({
        display: "flex",
        alignItems: "center",
        gap: 12,
      })}
    >
      <Avatar src={user?._links?.thumbnail?.href} alt={user?.title} size={32} />
      <div
        className={style({
          display: "flex",
          flexDirection: "column",
          gap: 2,
        })}
      >
        <Text styles={style({ font: "ui", fontWeight: "bold" })}>
          {user?.title}
        </Text>
        <Text
          styles={style({
            font: "ui-sm",
            color: "detail",
          })}
        >
          {user?.email ?? user?.caption}
        </Text>
      </div>
      {children}
    </div>
  );
};

UserListItem.propTypes = {
  user: PropTypes.object,
  children: PropTypes.node,
};

export const StoryShareDialog = ({ story }) => {
  if (!story) {
    return null;
  }

  const ims = useStore(store, (state) => state.ims);

  const [users, setUsers] = useState({});

  useEffect(() => {
    if (story?.createdBy) {
      setUsers((prevUsers) => ({
        ...prevUsers,
        [story.createdBy]: { email: story.createdBy },
      }));
    }
  }, [story?.createdBy]);

  const usersList = useAsyncList({
    async load({ signal, filterText }) {
      try {
        const response = await suggest(filterText, {}, { signal });
        console.log("Response from suggest:", response);
        if (response.ok) {
          return {
            items: response?.data?._embedded?.suggestions?.filter(
              (user) =>
                selectedUsers.findIndex(
                  (selected) => selected.caption === user.caption,
                ) === -1 &&
                Object.values(users).findIndex(
                  (u) => u.caption === user.caption,
                ) === -1,
            ),
          };
        }
      } catch (error) {
        console.error("Error loading user suggestions:", error);
      }

      return { items: [] };
    },
    getKey: (item) => item.token,
  });

  const [selectedUsers, setSelectedUsers] = useState([]);

  const shareStory = useShareStory(story.id);
  const unshareStory = useUnshareStory(story.id);

  const [isSharePending, startShareTransition] = useTransition();

  const handleSelectionClear = useCallback(() => {
    setSelectedUsers([]);
    usersList.setFilterText("");
    usersList.setSelectedKeys(new Set());
  }, [usersList]);

  const handleShare = useCallback(() => {
    // Handle sharing the story via email
    startShareTransition(async () => {
      try {
        const accepted = await accept(selectedUsers.map((user) => user.token));
        const emails = accepted?.data?._embedded?.successes?.map(
          (success) => success.email,
        );

        await shareStory.mutateAsync({ email: emails });

        handleSelectionClear();
      } catch (error) {
        console.error("Error accepting user invitations:", error);
      }
    });
  }, [selectedUsers, story.id, handleSelectionClear]);

  const handleUnshare = (email) => {
    // Handle unsharing the story via email
    unshareStory.mutate({ email });
  };

  const handleSelectionChange = useCallback(
    (selectedKey) => {
      const selectedPerson = usersList.getItem(selectedKey);

      if (!selectedPerson) return;

      startTransition(() => {
        setSelectedUsers((prev) => [...new Set(prev).add(selectedPerson)]);
        usersList.setFilterText("");
        usersList.setSelectedKeys(new Set());
      });
    },
    [usersList],
  );

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const emails = [
      ...new Set(
        [story?.createdBy, ...(story?.sharedWith || [])].filter(Boolean),
      ),
    ];

    if (emails.length) {
      startTransition(async () => {
        try {
          const people = await getPeople(emails, { signal });
          const usersMap = people.filter(Boolean).reduce((acc, person) => {
            acc[person.email] = person;
            return acc;
          }, {});

          setUsers((prevUsers) => ({
            ...prevUsers,
            ...usersMap,
          }));
        } catch (error) {
          console.error("Error fetching people:", error);
        }
      });
    }

    return () => {
      controller.abort();
    };
  }, [story?.createdBy, story?.sharedWith]);

  return (
    <CustomDialog
      isDismissible
      isDisabled={isSharePending}
      styles={style({ width: 480 })}
    >
      {({ close }) => (
        <>
          <Heading styles={style({ font: "title-2xl", marginTop: 0 })}>
            {!!selectedUsers.length ? (
              <div
                className={style({
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                })}
              >
                <ActionButton
                  aria-label="Cancel selection"
                  isQuiet
                  onPress={handleSelectionClear}
                  styles={style({ marginStart: -16 })}
                >
                  <BackIcon />
                </ActionButton>
                <Text>Invite people</Text>
              </div>
            ) : (
              <Text>Share story</Text>
            )}
          </Heading>
          <Content
            styles={style({
              display: "flex",
              flexDirection: "column",
              gap: 48,
            })}
          >
            <div>
              <ComboBox
                label="Add people"
                items={usersList.items}
                inputValue={usersList.filterText}
                onInputChange={usersList.setFilterText}
                selectedKeys={null}
                onSelectionChange={handleSelectionChange}
                loadingState={usersList.loadingState}
                autoComplete="off"
                styles={style({
                  marginBottom: 32,
                })}
              >
                {(item) => (
                  <ComboBoxItem id={item.token} textValue={item.title}>
                    <Avatar
                      slot="avatar"
                      src={item?._links?.thumbnail?.href}
                      alt={item?.title}
                    />
                    <Text slot="label">{item?.title}</Text>
                    <Text slot="description">
                      {item?.email ?? item?.caption}
                    </Text>
                  </ComboBoxItem>
                )}
              </ComboBox>

              {!!selectedUsers.length && (
                <>
                  <div className={listStyle}>
                    {selectedUsers.filter(Boolean).map((user, index) => (
                      <UserListItem key={user.token ?? index} user={user}>
                        <ActionButton
                          aria-label="Remove user"
                          isQuiet
                          isDisabled={isSharePending}
                          onPress={() => {
                            setSelectedUsers((prev) =>
                              prev.filter((u) => u?.token !== user.token),
                            );
                          }}
                        >
                          <DeleteIcon />
                        </ActionButton>
                      </UserListItem>
                    ))}
                  </div>
                  <div
                    className={style({
                      display: "flex",
                      gap: 8,
                      marginTop: 48,
                    })}
                  >
                    <ButtonGroup
                      isDisabled={isSharePending}
                      styles={style({ marginStart: "auto" })}
                    >
                      <Button
                        variant="secondary"
                        onPress={handleSelectionClear}
                      >
                        Cancel
                      </Button>
                      <Button variant="accent" onPress={handleShare}>
                        Invite to edit
                      </Button>
                    </ButtonGroup>
                  </div>
                </>
              )}

              {!selectedUsers.length && (
                <div className={style({ marginTop: 32 })}>
                  <div className={style({ marginBottom: 12 })}>
                    <Text
                      styles={style({
                        font: "detail",
                        color: "neutral-subdued",
                      })}
                    >
                      Who has access
                    </Text>
                  </div>
                  <div className={listStyle}>
                    <UserListItem user={users[story.createdBy]}>
                      <span className={style({ color: "detail" })}>
                        {ims?.profile?.email === story?.createdBy
                          ? " (You)"
                          : " (Owner)"}
                      </span>
                    </UserListItem>
                    {story?.sharedWith?.map((userEmail, index) => (
                      <UserListItem key={index} user={users[userEmail]}>
                        <ActionButton
                          aria-label="Remove user"
                          isQuiet
                          onPress={() => handleUnshare(userEmail)}
                        >
                          <DeleteIcon />
                        </ActionButton>
                      </UserListItem>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Content>
          <CloseButton
            styles={style({
              position: "absolute",
              top: 12,
              insetEnd: 12,
            })}
          />
        </>
      )}
    </CustomDialog>
  );
};

StoryShareDialog.propTypes = {
  story: PropTypes.object.isRequired,
};
